/**
 * /admin — Admin Dashboard page.
 *
 * Binding contract: `docs/ia/admin-dashboard.md`.
 *
 * Archetype: eyebrow-title + stat-strip + activity-grid (summary-then-feeds).
 * The page answers "is the system healthy? what's moving?" in one downward
 * scan — the morning-glance test.
 *
 * Substrate (15-FG/30): <AdminLayout> wraps <Shell mode='admin'
 * densityMode='workspace'>. Primitives only from `@/components/ui`
 * (Heading / Eyebrow / Button / Card / Link). design.md tokens only —
 * no `bg-bg-card` / `bg-surface` / `rounded-2xl` / chromatic borders /
 * second-accent fills.
 *
 * v1 data wiring carve-out: `useAdminDashboardStats` is a typed mock-data
 * hook today. The shape mirrors what the real `/api/admin/stats` endpoint
 * will return when it ships. See TODO below.
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — all CTAs use Link.
 *   - Inv 15 (atoms from barrel) — Heading / Button / Card / Link / Eyebrow only.
 */

import { type ReactElement, type ReactNode } from 'react';
import { AdminLayout } from './layout.tsx';
import { useAdminDashboardStats } from './use-admin-dashboard-stats.ts';
import { Card, Heading, Link } from '@/components/ui';

// ─── Formatting helpers ────────────────────────────────────────────────────────

/** Em-dash — the loading-state placeholder. Never "Loading…". */
const EM_DASH = '—';

/** Format an integer with locale thousands separators. */
function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

/** Format pass rate (0..1) as a rounded percentage.
 *  null = no aggregate available → em-dash. */
function formatPassRate(rate: number | null): string {
  if (rate === null) return EM_DASH;
  return `${Math.round(rate * 100)}%`;
}

// ─── Composition helpers — co-located per issue spec ──────────────────────────

interface StatCardProps {
  label: string;
  /** Pre-formatted value (already in Space Mono caller-side). */
  value: ReactNode;
}

/**
 * Stat card — pure type-on-card. No emoji, no chromatic top border, no
 * sparkline (Extension territory). Numeric value in Space Mono at the
 * workspace display scale; label in Space Mono 11px uppercase.
 */
function StatCard({ label, value }: StatCardProps): ReactElement {
  return (
    <Card variant="default" className="flex flex-col gap-3 p-6">
      <span className="font-mono text-eyebrow uppercase tracking-wide text-muted">
        {label}
      </span>
      <span className="font-mono text-h2 leading-none text-ink">{value}</span>
    </Card>
  );
}

interface ActivityPanelProps {
  /** Stable id used to associate the region with its heading. */
  id: string;
  title: string;
  viewAllHref: string;
  /** Columns rendered as `<th scope='col'>`. */
  columns: string[];
  /** Optional className passed to the outer Card (e.g. col-span for grid). */
  className?: string;
  children: ReactNode;
}

/**
 * Activity panel — Card with header (title + View all link) and tabular body.
 * The panel is exposed as a landmark `region` named by its heading so the
 * IA-conformance test can address it semantically.
 */
function ActivityPanel({
  id,
  title,
  viewAllHref,
  columns,
  className,
  children,
}: ActivityPanelProps): ReactElement {
  const headingId = `${id}-heading`;
  return (
    <Card variant="default" className={`p-0${className ? ` ${className}` : ''}`}>
      <section role="region" aria-labelledby={headingId}>
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <Heading
            id={headingId}
            level="h2"
            className="text-title font-medium text-ink"
          >
            {title}
          </Heading>
          <Link
            to={viewAllHref}
            className="font-sans text-small text-muted no-underline hover:text-accent"
          >
            View all
          </Link>
        </header>
        <div className="overflow-hidden">
          <table className="w-full border-collapse text-small">
            <thead>
              <tr className="border-b border-line">
                {columns.map((col) => (
                  <th
                    key={col}
                    scope="col"
                    className="px-6 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            {children}
          </table>
        </div>
      </section>
    </Card>
  );
}

interface PanelLoadingRowsProps {
  columnCount: number;
  /** Render N rows of em-dashes — at least one. */
  rows?: number;
}

function PanelLoadingRows({ columnCount, rows = 3 }: PanelLoadingRowsProps): ReactElement {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i} className="border-b border-line last:border-b-0">
          {Array.from({ length: columnCount }, (_, j) => (
            <td
              key={j}
              className="px-6 py-3 font-mono text-mono text-muted"
            >
              {EM_DASH}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

interface PanelEmptyRowProps {
  columnCount: number;
  children: ReactNode;
}

function PanelEmptyRow({ columnCount, children }: PanelEmptyRowProps): ReactElement {
  return (
    <tbody>
      <tr>
        <td
          colSpan={columnCount}
          className="px-6 py-10 text-center font-sans text-small text-muted"
        >
          {children}
        </td>
      </tr>
    </tbody>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function AdminDashboardPage(): ReactElement {
  const data = useAdminDashboardStats();
  const isLoading = data.status === 'loading' || data.stats === null;

  const statValues = isLoading
    ? { questions: EM_DASH, tests: EM_DASH, attempts: EM_DASH, pass_rate: EM_DASH }
    : {
        questions: formatInt(data.stats!.questions),
        tests: formatInt(data.stats!.tests),
        attempts: formatInt(data.stats!.attempts),
        pass_rate: formatPassRate(data.stats!.pass_rate),
      };

  return (
    <AdminLayout title="Dashboard" eyebrow="BIMASSESS – CONSOLE">
      {/* Header CTA cluster — right-aligned, sits beside the title block.
          AdminLayout already renders the title + eyebrow; we anchor the
          "+ New Test" CTA into the space below it, top-right, matching the
          contract's cluster placement (sandbox line 326). The 24px top
          margin establishes the header→strip rhythm. */}
      <div className="mt-6 flex items-start justify-end">
        <Link
          to="/admin/tests/new"
          className="inline-flex items-center justify-center rounded-md bg-ink px-5 py-2.5 font-sans text-small font-medium text-bg no-underline hover:bg-ink-2 hover:text-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-l focus-visible:ring-offset-2"
        >
          + New Test
        </Link>
      </div>

      {/* Stat strip — 4 metric cards, 20px gap between cards (Polish 2).
          Wrap at small viewports; the contract is desktop-first.
          Region-labelled so the IA-conformance test can scope its
          ordering assertions without colliding with column headers in
          the activity grid below (which also carry "Questions"). */}
      <section
        aria-label="System overview"
        className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
      >
        <StatCard label="Questions" value={statValues.questions} />
        <StatCard label="Tests" value={statValues.tests} />
        <StatCard label="Attempts" value={statValues.attempts} />
        <StatCard label="Pass Rate" value={statValues.pass_rate} />
      </section>

      {/* Activity grid — two panels side-by-side at the workspace breakpoint.
          The contract specifies a 1.3fr / 1fr proportion (Polish 2); a 7-col
          grid with 4/3 spans renders 4:3 = 1.33:1, which matches within the
          contract's rounding tolerance and avoids arbitrary-value drift.
          24px gap (Polish 2). Stacks vertically below lg so the dominant
          panel (Recent Tests) stays leftmost-or-topmost per Extension rules. */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-7">
        <ActivityPanel
          id="recent-tests"
          title="Recent Tests"
          viewAllHref="/admin/tests"
          columns={['Title', 'Questions', 'Attempts', 'Status']}
          className="lg:col-span-4"
        >
          {isLoading || data.recent_tests === null ? (
            <PanelLoadingRows columnCount={4} />
          ) : data.recent_tests.length === 0 ? (
            <PanelEmptyRow columnCount={4}>
              No recent tests — when an admin publishes a Test, it surfaces here.
            </PanelEmptyRow>
          ) : (
            <tbody>
              {data.recent_tests.map((row) => (
                <tr key={row.id} className="border-b border-line last:border-b-0">
                  <td className="px-6 py-3 font-sans text-ink">{row.title}</td>
                  <td className="px-6 py-3 font-mono text-mono text-ink-2">
                    {formatInt(row.questions)}
                  </td>
                  <td className="px-6 py-3 font-mono text-mono text-ink-2">
                    {formatInt(row.attempts)}
                  </td>
                  <td className="px-6 py-3 font-sans text-small text-muted">
                    {row.status}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </ActivityPanel>

        <ActivityPanel
          id="recent-results"
          title="Recent Results"
          viewAllHref="/admin/attempts"
          columns={['Name', 'Test', 'Score']}
          className="lg:col-span-3"
        >
          {isLoading || data.recent_results === null ? (
            <PanelLoadingRows columnCount={3} />
          ) : data.recent_results.length === 0 ? (
            <PanelEmptyRow columnCount={3}>
              No recent results — when a candidate submits an Attempt, it surfaces here.
            </PanelEmptyRow>
          ) : (
            <tbody>
              {data.recent_results.map((row) => (
                <tr key={row.id} className="border-b border-line last:border-b-0">
                  <td className="px-6 py-3 font-sans text-ink">{row.name}</td>
                  <td className="px-6 py-3 font-sans text-ink-2">{row.test}</td>
                  <td className="px-6 py-3 font-mono text-mono text-ink-2">
                    {row.score}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </ActivityPanel>
      </div>

    </AdminLayout>
  );
}
